<template>
    <b-card-body>
        <!-- Table Container Card -->
        <b-card no-body class="mb-0" data-test="pending-approval-nominator-table">
            <b-card-header tag="h1" class="border-bottom primary">
                <h1 class="mb-0">Pending Approval Nominator Changes</h1>
            </b-card-header>

            <b-card-body>
                <b-table
                    ref="refPendingApprovalNominatorListTable"
                    class="position-relative"
                    :items="pendingApprovalChangeNominators"
                    :fields="pendingApprovalNominatorTableColumns"
                    :current-page="pendingApprovalNominatorCurrentPage"
                    :per-page="pendingApprovalNominatorPerPage"
                    responsive
                    primary-key="id"
                    :sort-by.sync="sortPendingApprovalNominatorBy"
                    :sort-desc.sync="isSortDirDescPendingApprovalNominator"
                    busy.sync="isPendingApprovalNominatorBusy"
                    :filter="pendingApprovalNominatorSearchQuery"
                >
                    <template  #cell(index)="data" >
                        {{ data.index + 1 }}
                    </template>

                    <!-- Column: Actions -->
                    <template #cell(actions)= "data">
                        <div class="cursor-pointer">
                            <div v-if="isUserAdmin">
                                <b-dropdown size="sm" menu-class="" dropright variant="link" toggle-class="text-decoration-none" no-caret>
                                    <template #button-content>
                                        <feather-icon icon="MoreVerticalIcon"/>
                                    </template>

                                    <b-dropdown-item
                                        :data-test="`view-${data.item.nomineeShareholder.name}`"
                                        @click="view(data)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="EyeIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">View</span>
                                        </div>
                                    </b-dropdown-item>

                                    <b-dropdown-item
                                        :data-test="`approve-${data.item.nomineeShareholder.name}`"
                                        @click="approve(data.item)"
                                    >
                                        <div class="d-flex align-items-center">
                                            <feather-icon icon="CheckIcon" />
                                            <span style="padding-top: .1em; padding-left: .5em">Approve</span>
                                        </div>
                                    </b-dropdown-item>
                                </b-dropdown>
                            </div>

                            <div 
                                v-else 
                                class="d-flex align-items-center justify-content-end" 
                                :data-test="`view-${data.item.nomineeShareholder.name}`"
                                @click="view(data)"
                            >
                                <feather-icon icon="EyeIcon" />
                                <span style="padding-top: .1em; padding-left: .5em">View</span>
                            </div>
                        </div>
                    </template>
                </b-table>

                <loader v-if="isLoadingPendingApprovalNominator" data-type="Table" class="custom-loading" />

                <div class="mx-2 mb-2">
                    <b-row>
                        <b-col
                            cols="12"
                            sm="6"
                            class="
                                d-flex
                                align-items-center
                                justify-content-center justify-content-sm-start
                            "
                        >
                            <span class="text-muted"
                                >Showing {{ PendingApprovalNominatorDataMeta.from }} to {{ PendingApprovalNominatorDataMeta.to }} of
                                {{ PendingApprovalNominatorDataMeta.of }} entries</span
                            >
                        </b-col>
                        <!-- Pagination -->
                        <b-col
                            cols="12"
                            sm="6"
                            class="
                                d-flex
                                align-items-center
                                justify-content-center justify-content-sm-end
                            "
                        >
                            <b-pagination
                                v-model="pendingApprovalNominatorCurrentPage"
                                :total-rows="totalPendingApprovalNominator"
                                :per-page="pendingApprovalNominatorPerPage"
                                first-number
                                last-number
                                class="mb-0 mt-1 mt-sm-0"
                                prev-class="prev-item"
                                next-class="next-item"
                            >
                                <template #prev-text>
                                    <feather-icon
                                        icon="ChevronLeftIcon"
                                        size="18"
                                    />
                                </template>
                                <template #next-text>
                                    <feather-icon
                                        icon="ChevronRightIcon"
                                        size="18"
                                    />
                                </template>
                            </b-pagination>
                        </b-col>
                    </b-row>
                </div>
            </b-card-body>
        </b-card>
    </b-card-body>
</template>

<script>
import {
    BCard,
    BCardHeader,
    BCardBody,
    BRow,
    BCol,
    BTable,
    BPagination,
    BDropdown,
    BDropdownItem,
} from "bootstrap-vue";
import { mapGetters } from "vuex";

export default {
    components: {
        BRow,
        BCol,
        BCard,
        BTable,
        BDropdown,
        BCardBody,
        BPagination,
        BCardHeader,
        BDropdownItem,
    },

    props: {
        payloadForPendingChangeNominatorTable: {
            type: Object,
            required: true
        },
        
        pendingApprovalChangeNominators: {
            type: Array,
            required: true,
        }
    },

    computed: {
        ...mapGetters({
            isUserAdmin: "auth/isAdmin",
        }),
    },
    
    methods:{
        view(nomineeShareholder) {
            this.$emit('view', nomineeShareholder, "view");
        },

        approve(nomineeShareholder) {
            this.$emit('approve', nomineeShareholder, "approve");
        },
    },

    setup(props) {
        const {
            sortPendingApprovalNominatorBy,
            isPendingApprovalNominatorBusy,
            pendingApprovalNominatorPerPage,
            PendingApprovalNominatorDataMeta,
            isLoadingPendingApprovalNominator,
            pendingApprovalNominatorSearchQuery,
            pendingApprovalNominatorCurrentPage,
            pendingApprovalNominatorTableColumns,
            isSortDirDescPendingApprovalNominator,
            totalPendingApprovalNominator,
            pendingApprovalNominatorPerPageOptions,
            refPendingApprovalNominatorListTable,

            refetchPendingApprovalNominatorData,
            filterOnPendingApprovalNominator,
        } = props.payloadForPendingChangeNominatorTable;

        return {
            sortPendingApprovalNominatorBy,
            isPendingApprovalNominatorBusy,
            pendingApprovalNominatorPerPage,
            PendingApprovalNominatorDataMeta,
            isLoadingPendingApprovalNominator,
            pendingApprovalNominatorSearchQuery,
            pendingApprovalNominatorCurrentPage,
            pendingApprovalNominatorTableColumns,
            isSortDirDescPendingApprovalNominator,
            totalPendingApprovalNominator,
            pendingApprovalNominatorPerPageOptions,
            refPendingApprovalNominatorListTable,

            refetchPendingApprovalNominatorData,
            filterOnPendingApprovalNominator,
        };
    }
};
</script>

<style lang="scss" scoped>
    .table-responsive {
        overflow-x: inherit;
    }
</style>